
import React from 'react';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Globe, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LanguageGateProps {
  children: React.ReactNode;
}

const LanguageGate: React.FC<LanguageGateProps> = ({ children }) => {
  const { language, updateConfig, loading } = useAppConfig();
  const { i18n } = useTranslation();

  const handleLanguageSelect = async (selected: 'en' | 'es' | 'pt') => {
    try {
      await i18n.changeLanguage(selected);
      // Update config which handles Firestore sync
      await updateConfig({ language: selected });
      // Force localStorage sync just in case
      localStorage.setItem('i18nextLng', selected);
    } catch (error) {
      console.error('Failed to set language:', error);
    }
  };

  if (loading) return null;

  if (language) {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-xl">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-background to-blue-500/10 pointer-events-none" />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-md p-8 bg-card border border-border/50 shadow-2xl rounded-3xl overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600" />
        
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Globe className="h-8 w-8 text-white animate-pulse" />
          </div>
          
          <div className="space-y-2">
            <h1 className="text-3xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/60">
              Welcome to Bassline
            </h1>
            <p className="text-muted-foreground font-medium">
              Choose your preferred language to begin.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 w-full pt-4">
            <Button
              size="lg"
              variant="outline"
              onClick={() => handleLanguageSelect('es')}
              className="group relative h-16 text-lg font-bold border-2 hover:border-purple-600 transition-all duration-300"
            >
              <span className="flex items-center justify-between w-full px-4">
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🇪🇸</span>
                  Español (Latinoamérica)
                </span>
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </span>
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={() => handleLanguageSelect('en')}
              className="group relative h-16 text-lg font-bold border-2 hover:border-blue-600 transition-all duration-300"
            >
              <span className="flex items-center justify-between w-full px-4">
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🇺🇸</span>
                  English (International)
                </span>
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </span>
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={() => handleLanguageSelect('pt')}
              className="group relative h-16 text-lg font-bold border-2 hover:border-emerald-600 transition-all duration-300"
            >
              <span className="flex items-center justify-between w-full px-4">
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🇧🇷</span>
                  Português (Brasil)
                </span>
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </span>
            </Button>
          </div>

          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-black pt-4">
            You can change this anytime in settings
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LanguageGate;
